import { CopyOutlined } from "@ant-design/icons";
import { Button, Drawer, Flex, Input, message } from "antd";
import { useCCEntityOptional } from "components/CC/context/CCEntityContext.tsx";
import { CC_ENTITY_CONFIGS } from "components/CC/types/entityConfig.ts";
import { adaptBlueprintForEntity, BlueprintData, remapStepReferences } from "components/CC/utils/adaptBlueprint.ts";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useCallback, useEffect, useState } from "react";

interface BlueprintDrawerProps {
    open: boolean;
    onClose: () => void;
    value: BlueprintData;
    onApply: (data: BlueprintData) => void;
}

/**
 * Clean blueprint data for export - removes internal properties like beId
 */
const cleanForExport = (data: BlueprintData): BlueprintData => {
    const remapped = remapStepReferences(data);
    return {
        nodes: remapped.nodes.map((node) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { beId: _beId, ...cleanData } = (node.data || {}) as Record<string, unknown>;
            return {
                ...node,
                data: cleanData,
            };
        }),
        edges: remapped.edges.map((edge) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { beId: _beId, ...cleanEdgeData } = (edge.data || {}) as Record<string, unknown>;
            return {
                id: edge.id,
                source: edge.source,
                target: edge.target,
                type: edge.type,
                ...(Object.keys(cleanEdgeData).length > 0 && { data: cleanEdgeData }),
            };
        }),
    };
};

export const BlueprintDrawer = ({ open, onClose, value, onApply }: BlueprintDrawerProps) => {
    const { t } = usePaldockTranslation("components.connection-creator");
    const { t: tC } = usePaldockTranslation("common");
    const ccEntity = useCCEntityOptional();
    const [content, setContent] = useState("");
    const [isValid, setIsValid] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Sync internal state with external value when drawer opens
    useEffect(() => {
        if (open) {
            const cleanData = cleanForExport(value);
            setContent(JSON.stringify(cleanData, null, 2));
            setIsValid(true);
            setError(null);
        }
    }, [open, value]);

    // Validate JSON structure as user types — entity-rule mismatches are handled by adaptation on apply
    const handleContentChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newContent = e.target.value;
            setContent(newContent);

            try {
                const parsed = JSON.parse(newContent) as BlueprintData;
                if (!parsed.nodes || !Array.isArray(parsed.nodes)) {
                    setIsValid(false);
                    setError(t("blueprint.error_invalid_nodes"));
                    return;
                }
                if (!parsed.edges || !Array.isArray(parsed.edges)) {
                    setIsValid(false);
                    setError(t("blueprint.error_invalid_edges"));
                    return;
                }
                setIsValid(true);
                setError(null);
            } catch {
                setIsValid(false);
                setError(t("blueprint.error_invalid_json"));
            }
        },
        [t]
    );

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(content);
        message.success(t("blueprint.copied"));
    }, [content, t]);

    const handleApply = useCallback(() => {
        let parsed: BlueprintData;
        try {
            parsed = JSON.parse(content) as BlueprintData;
        } catch {
            message.error(t("blueprint.error_invalid_json"));
            return;
        }

        let blueprint = parsed;
        if (ccEntity) {
            const config = CC_ENTITY_CONFIGS[ccEntity.entityType];
            const { blueprint: adapted, warnings } = adaptBlueprintForEntity(parsed, ccEntity.entityType, config);
            blueprint = adapted;
            warnings.forEach((w) => message.warning(t(`blueprint.${w.key}`, w.params)));
        }

        onApply(blueprint);
        onClose();
        message.success(t("blueprint.applied"));
    }, [content, ccEntity, onApply, onClose, t]);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={<DrawerTitle mainTitle={t("blueprint.title")} />}
            width={700}
            extra={
                <Flex gap="small">
                    <Button size="large" icon={<CopyOutlined />} onClick={handleCopy}>
                        {tC("copy")}
                    </Button>
                    <Button size="large" onClick={onClose}>
                        {tC("cancel")}
                    </Button>
                    <Button size="large" type="primary" onClick={handleApply} disabled={!isValid}>
                        {tC("apply")}
                    </Button>
                </Flex>
            }
        >
            <DrawerContent>
                <p className="text-gray-500 mb-4">{t("blueprint.description")}</p>
                <Input.TextArea
                    value={content}
                    onChange={handleContentChange}
                    rows={25}
                    status={!isValid ? "error" : undefined}
                    style={{ fontFamily: "monospace", fontSize: 12 }}
                />
                {error && <p className="text-red-500 mt-2">{error}</p>}
            </DrawerContent>
        </Drawer>
    );
};
